import { useEffect, useState } from 'react';

function secondsUntil(deadlineMs: number | null): number {
  if (deadlineMs == null) return 0;
  return Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
}

/**
 * Countdown derived from a server-provided absolute deadline (ms since epoch)
 * rather than a local "count down from N" timer. This makes the remaining time
 * identical for every client regardless of when they joined, and immune to
 * clock drift or a refresh mid-round.
 *
 * It also re-syncs whenever the tab becomes visible again / regains focus /
 * comes back online, so a backgrounded tab shows the correct time the instant
 * it returns to the foreground (background timers are throttled by the browser).
 */
export function useDeadline(deadlineMs: number | null): number {
  const [secondsLeft, setSecondsLeft] = useState<number>(() => secondsUntil(deadlineMs));

  useEffect(() => {
    if (deadlineMs == null) {
      setSecondsLeft(0);
      return;
    }

    const tick = () => setSecondsLeft(secondsUntil(deadlineMs));
    tick();

    const interval = setInterval(tick, 500);
    const resync = () => {
      if (document.visibilityState === 'visible') tick();
    };

    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', tick);
    window.addEventListener('online', tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', tick);
      window.removeEventListener('online', tick);
    };
  }, [deadlineMs]);

  return secondsLeft;
}
